import { useMemo } from 'react';

import type { NumberingSeries } from '~/types/user';

import { useUser } from '~/api/user';
import { AlertDialog, Collapsible, useTable } from '~/components';

import type { NumberingSeriesTableProps as Props } from './types';

import { ConfirmDeleteNumberingSeriesDialog } from './ConfirmDeleteNumberingSeriesDialog';
import { EditNumberingSeriesForm } from './EditNumberingSeriesForm';
import { useNumberingSeriesTableColumns } from './NumberingSeriesTable.hooks';
import { expandWrapperStyle } from './NumberingSeriesTableCells/numberingSeriesCell.css';

export const NumberingSeriesTable = ({ expandedNumberingSeries, setExpandedNumberingSeries }: Props) => {
  const { data: user } = useUser();

  const deleteNumberingSeriesHandle = AlertDialog.useHandle<NumberingSeries>();
  const dialogHandles = useMemo(() => ({ deleteNumberingSeriesHandle }), [deleteNumberingSeriesHandle]);
  const actions = useMemo(
    () =>
      ({
        onEditNameAndType: ({ id }) => setExpandedNumberingSeries({ id, section: 'name_and_type' }),
        onEditFormatAndOptions: ({ id }) => setExpandedNumberingSeries({ id, section: 'format_and_options' }),
      }) satisfies Record<string, (numberingSeries: NumberingSeries) => void>,
    [setExpandedNumberingSeries],
  );

  const columns = useNumberingSeriesTableColumns({ dialogHandles, actions });

  const [table, Table] = useTable({
    data: user.numberingSeries,
    columns,
  });

  return (
    <>
      <Table.Root table={table}>
        <Table.Grid>
          <Table.Header />
          <Table.Body>
            {(row) => (
              <>
                <Table.Row row={row} />

                <Collapsible className={expandWrapperStyle}>
                  {expandedNumberingSeries?.id === row.original.id && (
                    <EditNumberingSeriesForm
                      numberingSeries={row.original}
                      onClose={() => setExpandedNumberingSeries(null)}
                      section={expandedNumberingSeries.section}
                    />
                  )}
                </Collapsible>
              </>
            )}
          </Table.Body>
        </Table.Grid>

        <Table.Pagination />
      </Table.Root>

      <AlertDialog.Root handle={deleteNumberingSeriesHandle}>
        {({ payload: numberingSeries }) =>
          numberingSeries && (
            <ConfirmDeleteNumberingSeriesDialog numberingSeries={numberingSeries} onClose={() => deleteNumberingSeriesHandle.close()} />
          )
        }
      </AlertDialog.Root>
    </>
  );
};
